import { memo, useCallback } from 'react';

import { useTrackAgentPanelOpened, useTrackAgentThreadListOpened } from '@air/analytics';

import { WorkspaceAgentHeaderStartTaskSplitButton } from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/WorkspaceAgentHeaderStartTaskSplitButton';
import { useIsWorkspaceAgentPanelAvailable } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useIsWorkspaceAgentPanelAvailable';
import {
  isWorkspaceAgentPanelExpanded,
  useWorkspaceAgentPanelStore,
} from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';

/**
 * Page-chrome way into the workspace agent panel: "Start task" opens chat with the composer
 * focused (a one-way action, so only its fill reflects the open chat); the icon half toggles
 * the tasks view and carries `aria-pressed`. The leaf owns the availability gate so a header
 * cannot advertise a panel that stands down. Callers must not mount this on non-`ui2026` chrome —
 * `ui2026` is not folded into `useIsWorkspaceAgentPanelAvailable` because the asset-modal Tasks
 * leaf still ships without that chrome.
 */
export const PrivateWorkspaceAgentHeaderTasksButton = memo(() => {
  const dock = useWorkspaceAgentPanelStore((state) => state.dock);
  const panelView = useWorkspaceAgentPanelStore((state) => state.panelView);
  const showChat = useWorkspaceAgentPanelStore((state) => state.showChat);
  const toggleTasks = useWorkspaceAgentPanelStore((state) => state.toggleTasks);
  const { isWorkspaceAgentPanelAvailable } = useIsWorkspaceAgentPanelAvailable();
  const { trackAgentPanelOpened } = useTrackAgentPanelOpened();
  const { trackAgentThreadListOpened } = useTrackAgentThreadListOpened();

  const handleStartTask = useCallback(() => {
    const wasMinimized = dock === 'minimized';
    showChat();

    if (wasMinimized) {
      trackAgentPanelOpened({ location: 'header' });
    }
  }, [dock, showChat, trackAgentPanelOpened]);

  const handleOpenTasks = useCallback(() => {
    const wasMinimized = dock === 'minimized';
    const wasOnTasks = !wasMinimized && panelView === 'tasks';
    toggleTasks();

    if (wasMinimized) {
      trackAgentPanelOpened({ location: 'header' });
    }

    if (!wasOnTasks) {
      trackAgentThreadListOpened({ location: 'header' });
    }
  }, [dock, panelView, toggleTasks, trackAgentPanelOpened, trackAgentThreadListOpened]);

  if (!isWorkspaceAgentPanelAvailable) {
    return null;
  }

  const isExpanded = isWorkspaceAgentPanelExpanded({ dock });

  return (
    <WorkspaceAgentHeaderStartTaskSplitButton
      isShowingChat={isExpanded && panelView === 'chat'}
      isShowingTasks={isExpanded && panelView === 'tasks'}
      onOpenTasks={handleOpenTasks}
      onStartTask={handleStartTask}
    />
  );
});

PrivateWorkspaceAgentHeaderTasksButton.displayName = 'PrivateWorkspaceAgentHeaderTasksButton';
